import * as Yup from 'yup';

import {
  aopDeductibleIsAlwaysPercent,
  noWindHailDeductibleStates,
  windHailDeductibleCanVaryByCounty
} from '@ourbranch/lookups';

import { validHomePurchaseDate } from 'common/helpers/yup-helpers';
import { currencyFormatter } from 'core/helpers/formatters';

Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);

const schema = (state) => {
  return Yup.object().shape({
    home: Yup.object().shape({
      purchaseDate: Yup.string().validHomePurchaseDate('home.purchaseDate', false),
      roofYear: Yup.number()
        .positive()
        .integer()
        .test('roofYearMin', 'Roof year must be higher than 1899', (value) => value >= 1899)
        .test(
          'roofYearMax',
          'Roof year cannot be greater than current year',
          (value) => value <= new Date().getUTCFullYear()
        )
        .test('is-older-than-year-built', 'Roof year cannot be older than home built year', function (value) {
          const { yearBuilt } = this.parent;
          if (!yearBuilt) {
            return true;
          }
          return value >= yearBuilt;
        }),
      sqFt: Yup.number()
        .positive()
        .integer()
        .test('sqFt-must-be-greater-than-0', 'sqFt must be a positive number', (val) => {
          return Number(val) && val > 0;
        })
    }),
    homeCoverage: Yup.object().shape({
      coverageA: Yup.number().test(
        'between-100-and-120-percent-rce',
        'RCE must be between 100% and 120%',
        function (value) {
          const rce = this.options.context?.home?.replacementCostEstimate?.total;

          if (!rce) {
            return true;
          }

          const rceFormatted = currencyFormatter(rce, { removeDecimal: true });
          const rce120Percent = rce * 1.2;

          // Determine state-specific max dwelling limit.
          // Mirrors home-coverage.jsx — keep both in sync.
          const stateMaxLimit = state === 'TX' || state === 'PA' ? 950000 : 1250000;

          // When RCE exceeds the state max, the soft 50012 rejection fires and the
          // user needs to walk back home characteristics to clear it. Skip Cov A
          // bounds validation in that case so the form can save — otherwise min
          // (rce) > max (stateMaxLimit) and no value is acceptable.
          if (rce > stateMaxLimit) {
            return true;
          }

          const actualMax = Math.min(rce120Percent, stateMaxLimit);
          const maxFormatted = currencyFormatter(actualMax, { removeDecimal: true });

          if (value < rce || value > actualMax) {
            // Use different message if state max is lower than 120% RCE
            const message =
              actualMax < rce120Percent
                ? `Coverage adjustment is out of range. You may adjust this from ${rceFormatted} (100% RCE) to ${maxFormatted} (maximum Cov A in this state)`
                : `Coverage adjustment is out of range. You may adjust this from ${rceFormatted} (100% RCE) to ${maxFormatted} (120% RCE).`;

            return this.createError({ message });
          }
          return true;
        }
      ),
      deductibleWindHail: Yup.number()
        .nullable()
        .test(
          'CompareToAllOtherPerils',
          'Wind & Hail Deductible must be greater than or equal to all other perils deductible',
          function (value) {
            const windHailExclusion = this.options.context?.homeCoverage?.windHailExclusion;
            if (windHailExclusion) {
              // force pass since no need to validiate this if excluded
              return true;
            }
            const hasHomeSelected = this.options.context?.selectedOption?.includes('H');
            const county = this.options.context?.home?.county?.toUpperCase();
            const windHailInState =
              (!windHailDeductibleCanVaryByCounty[state] ||
                (windHailDeductibleCanVaryByCounty[state] && windHailDeductibleCanVaryByCounty[state][county])) &&
              !noWindHailDeductibleStates[state];

            if (hasHomeSelected && windHailInState) {
              const coverageA = this.options.context.homeCoverage?.coverageA;
              const windHailIsPercent = value < 1;
              const allOtherPerilsIsPercent = this.options.context.homeCoverage?.deductibleAllOther < 1;

              // since value is a %, we need to multiply it by coverage A to see the deductible amount
              const allOtherPerilsAmount = allOtherPerilsIsPercent
                ? this.options.context.homeCoverage?.deductibleAllOther * coverageA
                : this.options.context.homeCoverage?.deductibleAllOther;
              const windHailAmount = windHailIsPercent ? value * coverageA : value;

              return windHailAmount >= allOtherPerilsAmount;
            }
            return true;
          }
        ),
      deductibleAllOther: Yup.number()
        .nullable()
        .test(
          'WindHailPercentageMin',
          'All Other Perils deductible must be a percentage for this state',
          function (value) {
            const hasHomeSelected = this.options.context?.selectedOption?.includes('H');
            if (hasHomeSelected && aopDeductibleIsAlwaysPercent[state] && value > 1) {
              return false;
            }
            return true;
          }
        )
    })
  });
};

export default schema;
